/**
 * useStartTask — calls complete-task with action='start'.
 *
 * Cleaner-initiated task lifecycle step. The cleaner taps "Start
 * cleaning" on the Task Detail page when they begin physical work;
 * this hook fires the RPC that:
 *   - validates role + ownership + org + state='pending'
 *   - walks the room state to cleaning_in_progress (1 or 2 hops)
 *   - bumps cleaning_tasks.status='in_progress' + started_at=now()
 *   - emits task.started audit
 *
 * Same AppError-shaped failure path as useSubmitTask so the UI can
 * render inline blocking errors (CLAUDE.md ERROR 4).
 */

import { useMutation, useQueryClient, type UseMutationResult } from '@tanstack/react-query'
import { useSupabase }                                          from './useSupabase'
import { AppError }                                             from '@/lib/errors/AppError'

interface StartVars {
  taskId: string
}

export interface StartResult {
  task_id:     string
  task_status: 'in_progress'
  started_at:  string
  room: {
    room_id:               string
    previous_status:       string
    new_status:            string
    ai_coverage_override:  boolean
  }
}

export function useStartTask(): UseMutationResult<StartResult, AppError, StartVars> {
  const supabase    = useSupabase()
  const queryClient = useQueryClient()

  return useMutation<StartResult, AppError, StartVars>({
    mutationFn: async ({ taskId }): Promise<StartResult> => {
      const { data, error } = await supabase.functions.invoke<StartResult>('complete-task', {
        body:    { task_id: taskId, action: 'start' },
        headers: { 'Idempotency-Key': crypto.randomUUID() },
      })
      if (error) {
        const ctx = error as unknown as { context?: Response }
        let code   = 'START_FAILED'
        let detail = error.message
        try {
          const parsed = await ctx.context?.clone().json() as { error?: string; detail?: string }
          if (parsed?.error) code = parsed.error
          if (parsed?.detail) detail = parsed.detail
        } catch { /* response was not JSON */ }
        throw new AppError({
          code,
          message:     detail,
          userMessage: userMessageFor(code, detail),
          status:      500,
        })
      }
      if (!data) {
        throw new AppError({
          code: 'EMPTY_RESPONSE', message: 'No data', userMessage: 'Start returned no result.', status: 500,
        })
      }
      return data
    },

    onSuccess: (_data, vars) => {
      void queryClient.invalidateQueries({ queryKey: ['task',     vars.taskId] })
      void queryClient.invalidateQueries({ queryKey: ['my-tasks']               })
      void queryClient.invalidateQueries({ queryKey: ['room-board']             })
      // Invalidate the cleaning-assistant grounded-context cache for this
      // task. Pre-claim the assistant call returns FORBIDDEN (cleaner is
      // not yet the assignee), and the error gets cached. After self-claim
      // the assignee branch authorizes — but without an invalidation here
      // the cached error sticks, the photo_requirements array stays empty,
      // and the PhotoUploader section silently doesn't render. Surfaced
      // 2026-05-19 ABC SMT demo: Maria tapped Start → page jumped straight
      // to Submit with no upload affordance. The cache invalidation fires
      // a fresh assistant call against the now-authorized state.
      void queryClient.invalidateQueries({ queryKey: ['cleaning-assistant', vars.taskId] })
      void queryClient.invalidateQueries({ queryKey: ['task-photos',         vars.taskId] })
    },
  })
}

function userMessageFor(code: string, detail: string): string {
  switch (code) {
    case 'INVALID_STATE':                return 'This task has already been started or is in another state.'
    case 'FORBIDDEN_ROLE':               return 'Your role is not allowed to start this task.'
    case 'FORBIDDEN_OWNERSHIP':          return 'You can only start tasks assigned to you.'
    case 'FORBIDDEN_ORG':                return 'This task does not belong to your organization.'
    case 'TASK_UNASSIGNED':              return 'This task has no assigned cleaner. Ask a supervisor to assign you before starting.'
    case 'TASK_NOT_FOUND':               return 'This task could not be found.'
    case 'ROOM_NOT_FOUND':               return 'The room for this task could not be found.'
    case 'INVALID_ROOM_STATE_FOR_START': return 'The room is not in a state that allows cleaning to start. Ask your supervisor.'
    case 'RATE_LIMITED':                 return 'Too many actions in a row. Try again in a moment.'
    default:                             return detail || 'Could not start the task.'
  }
}
